import React, { useState, useRef } from 'react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { AlertTriangle, Zap, ChevronLeft, ChevronRight, Brain, TrendingUp, DollarSign, Target, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function BusinessProgram() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = 3;
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };
  const features = [{
    icon: Brain,
    title: 'Мислення підприємця'
  }, {
    icon: TrendingUp,
    title: 'Формула залучення клієнтів'
  }, {
    icon: DollarSign,
    title: 'Фінансова логіка рішень'
  }, {
    icon: Target,
    title: 'Системний маркетинг'
  }, {
    icon: Users,
    title: 'Управління людьми і процесами'
  }];
  const slides = [{
    id: 'survival',
    tag: 'Режим виживання',
    tagColor: 'red',
    title: 'Бізнес працює за',
    titleHighlight: 'програмою',
    subtitle: '90 % з них за програмою виживання',
    description: 'Світ бізнесу увійшов у період турбулентності. Старі підходи більше не дають стабільності. Більшість підприємців керують бізнесом у режимі виживання, навіть якщо обороти зростають.',
    note: 'Це типовий стан бізнесу без лідерської операційної системи.',
    image: 'https://cdn.magicpatterns.com/uploads/1DZyCPd9HCrm3SnUyf17WP/режим_виживання.png'
  }, {
    id: 'leadership',
    tag: 'Режим Лідерства',
    tagColor: 'blue',
    title: 'Настає інша програма',
    titleHighlight: 'Програма лідерства',
    subtitle: '',
    description: 'Лідерство часто сприймають як вроджений талант або як складний набір навичок. Цей міф зручний — він дозволяє не брати відповідальність. Насправді лідерство – це усвідомлений вибір.',
    note: '',
    image: 'https://cdn.magicpatterns.com/uploads/wyACcAAhJebzcuNUnFBFep/Режим_лидера.png',
    transitions: [{
      from: 'Від реакції',
      to: 'До управління'
    }, {
      from: 'Від виживання',
      to: 'До лідерства'
    }]
  }, {
    id: 'system',
    tag: 'Операційна система',
    tagColor: 'blue',
    title: '«Шлях лідера» —',
    titleHighlight: 'операційна система для підприємця',
    subtitle: '',
    description: '«Шлях лідера» допомагає змінити мислення підприємця: з виживання — на лідерство',
    note: '',
    features: features
  }];
  return <section className="py-20 md:py-32 relative overflow-hidden" style={{
    backgroundColor: '#050810'
  }} data-id="element-19">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-20">
        {/* Navigation Arrows */}
        <div className="flex justify-between items-center mb-8" data-id="element-21">
          <button onClick={prevSlide} className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-id="element-22">
            <ChevronLeft className="w-6 h-6" data-id="element-23" />
          </button>

          {/* Slide Indicators */}
          <div className="flex gap-2" data-id="element-24">
            {slides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-3 h-3 rounded-full transition-colors ${currentSlide === index ? 'bg-blue-500' : 'bg-white/20'}`} data-id="element-25" />)}
          </div>

          <button onClick={nextSlide} className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-id="element-26">
            <ChevronRight className="w-6 h-6" data-id="element-27" />
          </button>
        </div>

        {/* Slides */}
        <AnimatePresence mode="wait" data-id="element-28">
          <motion.div key={currentSlide} initial={{
          opacity: 0,
          x: 100
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -100
        }} transition={{
          duration: 0.3
        }} data-id="element-29">
            {currentSlide === 0 && <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-30">
                <div className="space-y-6" data-id="element-31">
                  <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full border ${slides[0].tagColor === 'red' ? 'border-red-500/50 bg-red-500/10 text-red-400' : 'border-blue-500/50 bg-blue-500/10 text-blue-400'}`} data-id="element-32">
                    <AlertTriangle className="w-5 h-5" data-id="element-33" />
                    <span className="font-bold uppercase tracking-wider text-sm" data-id="element-34">
                      {slides[0].tag}
                    </span>
                  </div>

                  <h2 className="text-3xl md:text-5xl font-bold" data-id="element-35">
                    {slides[0].title}{' '}
                    <span className="text-red-500" data-id="element-36">
                      {slides[0].titleHighlight}
                    </span>
                  </h2>

                  <p className="text-xl text-gray-400" data-id="element-37">{slides[0].subtitle}</p>

                  <p className="text-gray-300 leading-relaxed" data-id="element-38">
                    {slides[0].description}
                  </p>

                  <p className="text-sm text-gray-500 italic border-l-2 border-red-500 pl-4" data-id="element-39">
                    {slides[0].note}
                  </p>

                  <Button href="#tariffs" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500" data-id="element-40">
                    Доєднатися до програми «Шлях Лідера»
                  </Button>
                </div>

                <div className="relative" data-id="element-41">
                  <img src={slides[0].image} alt="Режим виживання" className="w-full h-auto max-w-lg mx-auto" data-id="element-42" />
                </div>
              </div>}

            {currentSlide === 1 && <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-43">
                <div className="space-y-6" data-id="element-44">
                  <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-blue-500/50 bg-blue-500/10 text-blue-400" data-id="element-45">
                    <Zap className="w-5 h-5" data-id="element-46" />
                    <span className="font-bold uppercase tracking-wider text-sm" data-id="element-47">
                      {slides[1].tag}
                    </span>
                  </div>

                  <h2 className="text-3xl md:text-5xl font-bold" data-id="element-48">
                    {slides[1].title}
                    <br data-id="element-49" />
                    <span className="text-blue-400" data-id="element-50">
                      {slides[1].titleHighlight}
                    </span>
                  </h2>

                  <p className="text-gray-300 leading-relaxed" data-id="element-51">
                    {slides[1].description}
                  </p>

                  <div className="space-y-2" data-id="element-52">
                    {slides[1].transitions?.map((t, i) => <div key={i} className="flex items-center gap-3 text-gray-400" data-id="element-53">
                        <span data-id="element-54">{t.from}</span>
                        <span className="h-px flex-1 bg-gray-700" data-id="element-55"></span>
                        <span className="text-white font-medium" data-id="element-56">{t.to}</span>
                      </div>)}
                  </div>

                  <Button href="#tariffs" variant="primary" withArrow data-id="element-57">
                    Доєднатися до програми «Шлях Лідера»
                  </Button>
                </div>

                <div className="relative" data-id="element-58">
                  <img src={slides[1].image} alt="Режим лідерства" className="w-full h-auto max-w-lg mx-auto" data-id="element-59" />
                </div>
              </div>}

            {currentSlide === 2 && <div className="text-center" data-id="element-60">
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-blue-500/50 bg-blue-500/10 text-blue-400 mb-8" data-id="element-61">
                  <Zap className="w-5 h-5" data-id="element-62" />
                  <span className="font-bold uppercase tracking-wider text-sm" data-id="element-63">
                    {slides[2].tag}
                  </span>
                </div>

                <h2 className="text-3xl md:text-5xl font-bold mb-6 max-w-4xl mx-auto" data-id="element-64">
                  {slides[2].title}{' '}
                  <span className="text-blue-400" data-id="element-65">
                    {slides[2].titleHighlight}
                  </span>
                </h2>

                <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto" data-id="element-66">
                  {slides[2].description}
                </p>

                <div className="flex flex-wrap justify-center gap-4" data-id="element-67">
                  {slides[2].features?.map((feature, index) => <div key={index} className="flex items-center gap-4 bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl px-6 py-4" data-id="element-68">
                      <div className="bg-blue-500/20 p-3 rounded-xl" data-id="element-69">
                        <feature.icon className="w-6 h-6 text-blue-400" data-id="element-70" />
                      </div>
                      <span className="font-semibold text-lg" data-id="element-71">
                        {feature.title}
                      </span>
                    </div>)}
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}